<script>
import { Query, useQueryClient } from "@sveltestack/svelte-query"
import API from "@local/utils/api"
import { currentUser } from "@local/store/auth"
import { link } from "svelte-routing"
import Pager from "@local/components/Pager.svelte"
import EmptyMessage from "@local/components/EmptyMessage.svelte"
import Icon from "@local/components/images/Icon.svelte"
import MultiSelect from "@local/components/MultiSelect.svelte"
import BackToTop from "@local/components/BackToTop.svelte"

const queryClient = useQueryClient()

let perFilter = 20

let query = ""
let queryVal = ""
let reasons = []
let reasonsVal = []
let subdomains = []
let subdomainsVal = []
let tags = []
let tagsVal = []
let langs = []
let langsVal = []
let veterans = []
let veteransVal = []
let page = 0
let wfId
let isUploading = false

function search(pageNumber) {
  page = pageNumber
  query = queryVal
  reasons = reasonsVal
  subdomains = subdomainsVal
  tags = tagsVal
  langs = langsVal
  veterans = veteransVal
}

async function fetchWhoToCall(page, query, reasons, subdomains, tags) {
  const searchParams = {
    page,
    perFilter,
    query,
    reasons,
    subdomains,
    tags,
    langs,
    veterans,
    source: "wtc",
  }
  const response = await API.get(`admin/who-to-call`, { searchParams })
  const rows = await response.json()
  const hasNextPage = rows.length > 0
  const count = parseInt(response.headers.get("X-Total-Count"))

  return { rows, hasNextPage, query, count }
}

function clearFilters() {
  queryVal = ""
  reasonsVal = []
  subdomainsVal = []
  tagsVal = []
  langsVal = []
  veteransVal = []
  queryClient.invalidateQueries("whoToCallFilters")
  search(0)
}

async function handleUpload(evt = {}) {
  const inputFiles = evt.target.files

  const reader = new FileReader()
  reader.onload = (readerEvent) => {
    const file = {
      src: readerEvent.target.result,
      name: inputFiles[0].name,
    }

    isUploading = true
    API.patch(`admin/who-to-call`, {
      json: { csvBase64: file.src },
      timeout: 300000,
    })
      .then(() => {
        queryClient.invalidateQueries()
      })
      .catch((err) => {
        if (err.name === "TimeoutError") {
          alert(
            "Upload took too long and was cancelled (timeout). Try again later or send a smaller file."
          )
        } else {
          // alert(`UPDATE REGISTER FAIL: ${err}`)
          console.warn("WTC UPLOAD ERROR", err)
        }
      })
      .finally(() => {
        isUploading = false
      })
  }

  reader.readAsDataURL(inputFiles[0])
}

async function handleDel(id) {
  API.delete(`admin/who-to-call/${id}`)
    .then(() => {
      queryClient.invalidateQueries()
    })
    .catch((err) => alert(`FAILED REMOVING RECORD WITH ERROR ${err}`))
}

function buildDpLink(row) {
  let fromPhone = "+18552833985"
  const phone = row.phone
    .replaceAll(" ", "")
    .replaceAll("-", "")
    .replaceAll("+", "")

  return `dialpad://${phone}?launchMinimode=1&confirmPhone=1&fromNumber=+${fromPhone}&crmLogging=0&showDispositions=1&customData={row.wfId}`
}

$: queryOptions = {
  queryKey: [
    "whoToCall",
    page,
    query,
    reasons,
    subdomains,
    tags,
    langs,
    veterans,
  ],
  queryFn: () =>
    fetchWhoToCall(page, query, reasons, subdomains, tags, langs, veterans),
  keepPreviousData: true,
  refetchOnWindowFocus: false,
}

$: subdomainsOptions =
  ($currentUser.callerSubdomains || []).length > 0
    ? $currentUser.callerSubdomains
    : [
        "starseed",
        "mymedi",
        "pearson",
        "aurora",
        "tilray",
        "seniorcareroc",
        "massageaddict",
        "mendo",
        "herbaldispatch",
        "tantus",
        "cannafarms",
        "cccc",
        "ccct",
        "alternapharma",
        "aqualitas",
        "blackrose",
        "floweroflife",
        "vitalicann",
        "zyus",
      ]
</script>

<Query options="{queryOptions}">
  <div class="p-5" slot="query" let:queryResult>
    {#if queryResult.isLoading}
      <div class="animate-pulse"></div>
    {:else if queryResult.error}
      <EmptyMessage icon="error">{queryResult.error}</EmptyMessage>
    {:else}
      <div class="flex flex-row items-center justify-between mb-6">
        <h2 class="h2">
          Who to call - {queryResult.data.count} results found
        </h2>
      </div>
      <div class="flex w-full items-center gap-2 mb-2">
        <Pager
          bind:page="{page}"
          hasNextPage="{queryResult.data.hasNextPage}"
          isPreviousData="{queryResult.isPreviousData}"
        />
        <form
          class="flex items-center w-full"
          on:submit|preventDefault="{() => search(0)}"
        >
          <div
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 cursor-pointer btn btn-outline relative"
          >
            <input
              type="file"
              class="opacity-0 w-full h-full absolute top-0 left-0"
              on:change="{handleUpload}"
              disabled="{isUploading}"
            />
            <Icon size="3xl">upload_file</Icon>
          </div>
          {#if isUploading}
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            >
              <div
                class="bg-white rounded-lg shadow-lg p-8 flex flex-col items-center"
              >
                <Icon size="4xl" class="animate-spin text-blue-600 mb-4"
                  >upload_file</Icon
                >
                <span class="text-lg font-semibold text-gray-800"
                  >Uploading file, please wait...</span
                >
              </div>
            </div>
          {/if}
          {#key subdomainsVal.length === 0}
            <MultiSelect
              className="w-32 ml-2 w-full"
              bind:value="{subdomainsVal}"
              placeholder="Partners"
            >
              {#each subdomainsOptions as subdomain}
                <option value="{subdomain}">{subdomain}</option>
              {/each}
            </MultiSelect>
          {/key}
          <button
            title="Clear filters"
            class="flex items-center justify-center w-10 h-10 ml-2 btn btn-outline"
            on:click="{clearFilters}"
            type="button"
          >
            <Icon size="3xl">clear</Icon>
          </button>
          <button
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 ml-2 btn btn-outline"
            type="submit"><Icon size="3xl">search</Icon></button
          >
        </form>
      </div>
      {#if queryResult.data.rows.length > 0}
        <table class="table">
          <thead>
              <tr>
                <th>Patient</th>
                <th>Phone</th>
                <th>Origin</th>
                <th>Expiration</th>
                <th>Reason</th>
                <th></th>
                <th></th>
                <th></th>
            </tr>
          </thead>
          <tbody>
            {#each queryResult.data.rows as row}
              <tr class="hover:bg-gray-50 transition-colors duration-200">
                <td>{row.name}<br /><i>{row.email}</i></td>
                <td>{row.phone}</td>
                <td
                  >{row.subdomain}<br /><i
                    >{row.veteran === "Yes" ? "Veteran" : ""}</i
                  ></td
                >
                <td>{row.expiresAt}</td>
                <td>{row.reason}<br /><i>{row.purchaseTag}</i></td>
                <td>
                  <a use:link href="{buildDpLink(row)}" title="Call patient">
                    <Icon>phone_in_talk</Icon>
                  </a>
                </td>
                <td>
                  <button
                    type="button"
                    class="focus:outline-none"
                    on:click="{() => handleDel(row._id)}"
                    title="Remove from list"
                  >
                    <Icon>not_interested</Icon>
                  </button>
                </td>
                <td>
                  <a
                    use:link
                    href="users/{row.userId}"
                    title="Go to patient profile"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon>arrow_forward</Icon>
                  </a>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {:else}
        <EmptyMessage icon="check">No workflows at the moment</EmptyMessage>
      {/if}
    {/if}
  </div>
</Query>

<BackToTop />
